<script lang="ts">
	/**
	 * A product line, rendered as the ladder it actually is (Design §14).
	 *
	 * `rank` is real, enforced domain data — it is what decides whether a move between two products
	 * is an upgrade or a downgrade (§9.3) — and the catalogue used to throw it away, rendering an
	 * ordered ladder as a row of identical cards. Three equal cards say "three unrelated things";
	 * a ladder says "pick your rung", which is the decision the page exists to support.
	 *
	 * Two devices carry it, and no more than two:
	 *
	 * - **The ascent edge.** Each card's top edge is its position on the line, interpolated along
	 *   the gradient `app.postcss` already draws under every `h1` — cool at the entry tier, warm at
	 *   the top. Per-card rather than one rail across the row, because the row wraps: a rail drawn
	 *   behind a wrapping grid lands in the gutter at every breakpoint but the widest.
	 * - **The delta.** Every rung above the first leads with *what it adds over the one below*
	 *   rather than repeating the whole list. On a ladder the full feature list is the least useful
	 *   thing on the card: every rung's list is mostly the previous rung's list, so four bullets of
	 *   "Everything you already saw" is what the customer reads three times over.
	 *
	 * A **standalone product is not a rung** and must not be drawn as one: `isLadder` is false for
	 * the ungrouped set, which drops the ascent edge and the delta. Drawing an ordering onto
	 * products that have none is a lie the customer cannot check.
	 */

	import { Badge } from 'flowbite-svelte';
	import PriceDisplay from './PriceDisplay.svelte';
	import RoutedButton from './RoutedButton.svelte';
	import { mediaURL, rungColor } from '../Utilities';
	import type { Price, Product, ProductComparisonFeature, StorefrontCurrency } from '../AppTypes';

	/** The line's products. Sorted here by rank, so no caller has to remember to. */
	export let products: Product[] = [];
	export let currency: StorefrontCurrency | undefined = undefined;
	/**
	 * False for the ungrouped set, which has no ordering to show. It drops both devices rather
	 * than dimming them: a faint ascent is still an ascent.
	 */
	export let isLadder: boolean = true;

	$: rungs = [...products].sort((left, right) => left.rank - right.rank);

	function headline(product: Product): Price | null {
		return product.prices.find(price => price.isDefault) ?? product.prices[0] ?? null;
	}

	function isPromoted(product: Product): boolean {
		return typeof product.highlight === 'string' && product.highlight.length > 0;
	}

	/**
	 * One comparison row as a line of card copy: the amount when there is one, the label alone when
	 * there is not — because null is the tick, and a tick has nothing to add to its own label.
	 */
	function asLine(row: ProductComparisonFeature): string {
		return row.value === null ? row.label : `${row.label}: ${row.value}`;
	}

	/** Everything this tier gives, in the order it was written. */
	function allOf(product: Product): string[] {
		return (product.comparisonFeatures ?? []).map(asLine);
	}

	/**
	 * What this rung adds over the one below it.
	 *
	 * A **changed amount counts as added** — "Projects: 100" against ten is the single most common
	 * thing separating two rungs, and comparing labels alone would report it as no difference at
	 * all. That is the case the old `featureList` of bullets could not express, and the reason it
	 * is gone.
	 *
	 * Empty when there is nothing to say, and the card falls back to everything the tier gives. A
	 * rung whose rows are genuinely identical to the one below it has a pricing problem, not a
	 * rendering one.
	 */
	function addedOver(product: Product, previous: Product | undefined): string[] {
		if (previous === undefined) return [];

		const below = new Map((previous.comparisonFeatures ?? []).map(row => [row.label, row.value]));
		return (product.comparisonFeatures ?? [])
			.filter(row => !below.has(row.label) || below.get(row.label) !== row.value)
			.map(asLine);
	}
</script>

<div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
	{#each rungs as product, index (product.uuid)}
		{@const previous = isLadder ? rungs[index - 1] : undefined}
		{@const added = addedOver(product, previous)}
		{@const bullets = added.length > 0 ? added : allOf(product)}
		<article
			class="relative widget bg-depth border-widget widget-spotlight flex flex-col gap-3 overflow-hidden transition-transform"
			class:ring-2={isPromoted(product)}
			class:ring-primary-500={isPromoted(product)}
			class:xl:-translate-y-2={isPromoted(product)}
		>
			{#if isLadder}
				<!--
					The ascent. An inline style rather than a Tailwind class, because a class built at
					runtime is invisible to the JIT scanner and the rule silently never ships.
				-->
				<span
					class="absolute inset-x-0 top-0 h-1"
					style={`background-color: ${rungColor(index, rungs.length)}`}
					aria-hidden="true"
				/>
			{/if}

			<div class="flex items-start gap-3 {isLadder ? 'pt-2' : ''}">
				<!-- Only when there is one: an empty box beside every name reads as a failed image. -->
				{#if product.imageURL}
					<img
						src={mediaURL(product.imageURL)}
						alt=""
						class="w-12 h-12 rounded-xl object-contain shrink-0"
						loading="lazy"
					/>
				{/if}
				<div class="min-w-0">
					<div class="flex flex-wrap items-center gap-2">
						<h3 class="font-bold text-lg leading-tight">{product.name}</h3>
						{#if isPromoted(product)}
							<Badge color="blue" class="uppercase tracking-wide text-[10px]">
								<i class="fa-solid fa-star mr-1 text-[9px]" />{product.highlight}
							</Badge>
						{/if}
					</div>
					{#if product.tagline}
						<p class="text-sm text-default">{product.tagline}</p>
					{/if}
				</div>
			</div>

			<!-- Tabular figures, so a column of prices reads as a ladder rather than as a list. -->
			<PriceDisplay
				price={headline(product)}
				{currency}
				currencyCode={product.currency}
				class="tabular-nums"
			/>

			{#if bullets.length > 0}
				<div class="space-y-1.5">
					{#if added.length > 0 && previous !== undefined}
						<!--
							The one line that makes a ladder legible. Without it every rung repeats the
							rung below it, and the customer reads the same four bullets three times.
						-->
						<p class="card-label">
							<i class="fa-solid fa-plus text-[11px]" />Everything in {previous.name}, plus
						</p>
					{/if}
					<ul class="text-sm space-y-1.5">
						{#each bullets.slice(0, 4) as bullet (bullet)}
							<li class="flex items-start gap-2">
								<i class="fa-solid fa-check text-green-600 dark:text-green-400 mt-1 text-[11px]" />
								<span>{bullet}</span>
							</li>
						{/each}
					</ul>
					{#if bullets.length > 4}
						<p class="text-xs text-default pl-5">and {bullets.length - 4} more</p>
					{/if}
				</div>
			{/if}

			<div class="grow" />
			<RoutedButton
				href={`/Product/${product.slug}`}
				size="sm"
				color={isPromoted(product) ? 'primary' : 'alternative'}
				class="w-full"
			>
				View details
			</RoutedButton>
		</article>
	{/each}
</div>
